import { useQuery } from '@apollo/client/react';
import { DimeSpinner } from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { DepartmentDetailView } from '../../modules/departments/department-detail.js';
import {
  GET_DEPARTMENT,
  type GetDepartmentQueryResult,
  type GetDepartmentQueryVariables,
} from '../../modules/departments/operations.js';

export const Route = createFileRoute('/departments/$departmentId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: SetupDepartmentDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SetupDepartmentDetailPage() {
  const navigate = useNavigate();
  const { departmentId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  const { data, loading, refetch } = useQuery<
    GetDepartmentQueryResult,
    GetDepartmentQueryVariables
  >(GET_DEPARTMENT, { variables: { id: departmentId } });

  const department = data?.department;
  useBreadcrumbLabel(departmentId, department?.name);

  const setEditingInUrl = (editing: boolean) => {
    void navigate({
      to: '/departments/$departmentId',
      params: { departmentId },
      search: { edit: editing },
      replace: true,
    });
  };

  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <DimeSpinner />
          Loading…
        </div>
      ) : !department ? (
        <div className="text-sm text-muted-foreground">Department not found.</div>
      ) : (
        <DepartmentDetailView
          key={startEditing ? 'edit' : 'view'}
          department={department}
          initialEditing={startEditing}
          onChanged={() => void refetch()}
          onEditingChange={setEditingInUrl}
        />
      )}
    </div>
  );
}
